import { useEffect, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { CiSearch } from "react-icons/ci";
import { FiChevronRight, FiCopy, FiShare2, FiTrash2 } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { database } from "../constants/firebase";
import { ref, onValue, off, set } from "firebase/database";
import { ROUTES } from "../App";
import useMoveTo from "../constants/navigation";
import { useUser } from "../contexts/UserContext";
import { copyToClipboard, formatDate } from "../utils/utils";
import { ICONS } from "../constants/icons";
import MobileHeader from "../components/MobileHeader";

const MyLibrary = () => {
    const navigate = useMoveTo();
    const { currentUser } = useUser();
    const [searchQuery, setSearchQuery] = useState("");

    const [libraries, setLibraries] = useState([]);       // Purchased items
    const [bookmarks, setBookmarks] = useState([]);       // Bookmarked items

    const [users, setUsers] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadedImages, setLoadedImages] = useState({});

    const [mainTab, setMainTab] = useState("Purchased");

    const [contextMenu, setContextMenu] = useState({
        visible: false,
        x: 0,
        y: 0,
        link: ""
    });
    const [openMenuId, setOpenMenuId] = useState(null);

    // Close context menu
    useEffect(() => {
        const closeMenu = () => {
            setContextMenu(prev => ({ ...prev, visible: false }));
            setOpenMenuId(null);
        };
        window.addEventListener("click", closeMenu);
        return () => window.removeEventListener("click", closeMenu);
    }, []);

    // Load all users
    useEffect(() => {
        const usersRef = ref(database, "Users");
        const unsubscribe = onValue(usersRef, (snapshot) => {
            if (snapshot.exists()) setUsers(snapshot.val());
        });
        return () => off(usersRef, "value", unsubscribe);
    }, []);

    // Load purchased items
    useEffect(() => {
        if (!currentUser?.uid) return;

        const libRef = ref(database, `Users/${currentUser.uid}/library`);
        const unsubscribe = onValue(libRef, (snapshot) => {
            if (!snapshot.exists()) {
                setLibraries([]);
                setLoading(false);
                return;
            }

            const data = snapshot.val();
            const items = Object.keys(data).map((id) => ({
                id,
                productId: data[id].productId,
                savedAt: data[id].createdAt || null,
            }));

            // Load all listings
            const listingsRef = ref(database, "Listings");
            onValue(listingsRef, (listSnap) => {
                if (!listSnap.exists()) return;

                const allListings = listSnap.val();
                const merged = items
                    .map((libItem) => {
                        const product = allListings[libItem.productId];
                        if (!product) return null;
                        return { ...libItem, ...product };
                    })
                    .filter(Boolean);

                const sorted = merged.sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                );

                setLibraries(sorted);
                setLoading(false);
            });
        });

        return () => off(libRef, "value", unsubscribe);
    }, [currentUser]);

    // ⭐ Load bookmarked items
    useEffect(() => {
        if (!currentUser?.uid) return;

        const bmRef = ref(database, `Users/${currentUser.uid}/bookmarks`);
        const unsubscribe = onValue(bmRef, (snapshot) => {
            if (!snapshot.exists()) {
                setBookmarks([]);
                return;
            }

            const data = snapshot.val();
            const items = Object.keys(data).map((id) => ({
                id,
                ...data[id],
            }));

            // Load listings
            const listingsRef = ref(database, "Listings");
            onValue(listingsRef, (listSnap) => {
                if (!listSnap.exists()) return;

                const allListings = listSnap.val();
                const merged = items
                    .map((bmItem) => {
                        const product = allListings[bmItem.productId];
                        if (!product) return null;
                        return { ...bmItem, ...product };
                    })
                    .filter(Boolean);

                const sorted = merged.sort(
                    (a, b) => new Date(b.addedAt) - new Date(a.addedAt)
                );

                setBookmarks(sorted);
            });
        });

        return () => off(bmRef, "value", unsubscribe);
    }, [currentUser]);

    const handleImageLoad = (id) => {
        setLoadedImages((prev) => ({ ...prev, [id]: true }));
    };

    // Search filter — works for both Purchased and Bookmarked
    const listToShow = mainTab === "Purchased" ? libraries : bookmarks;

    const filteredList = listToShow.filter((item) => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
            item.title?.toLowerCase().includes(q) ||
            item.explainer?.toLowerCase().includes(q) ||
            (users && users[item.userId]?.name?.toLowerCase().includes(q))
        );
    });
    const handleShare = async (item) => {
        const shareLink = `${window.location.origin}/#${ROUTES.PREVIEW_PRODUCT_DETAILS.replace(':productId', item.productId)}`;
        
        if (navigator.share) {
            try {
                await navigator.share({
                    title: item.title,
                    text: `Check out this product: ${item.title}`,
                    url: shareLink,
                });
            } catch (error) {
                console.error("Error sharing:", error);
                copyToClipboard(shareLink);
                window.showToast("Link copied to clipboard!", "success");
            }
        } else {
            copyToClipboard(shareLink);
            window.showToast("Link copied to clipboard!", "success");
        }
    };

    const handleRemoveFromBookmarks = async (itemId) => {
        if (!currentUser?.uid) {
            window.showToast("Please login to remove items.", "error");
            return;
        }
        try {
            await set(ref(database, `Users/${currentUser.uid}/bookmarks/${itemId}`), null);
            window.showToast("Item removed from bookmarks.", "success");
        } catch {
            window.showToast("Failed to remove.", "error");
        }
    };

    // Skeleton
    const LibrarySkeleton = () => (
        <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
                <div
                    key={i}
                    className="flex items-center justify-between border-t border-gray-200 dark:border-[#D9D9D9]/20 px-4 py-4 rounded-lg animate-pulse"
                >
                    <div className="flex gap-4 max-w-[60%]">
                        <div className="w-16 h-16 bg-gray-300 dark:bg-gray-700 rounded-lg" />
                        <div className="flex-1 space-y-2">
                            <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/5" />
                            <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded w-full" />
                            <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded w-4/5" />
                        </div>
                    </div>
                    <div className="w-10 h-10 bg-gray-300 dark:bg-gray-700 rounded" />
                </div>
            ))}
        </div>
    );

    return (
        <div className="h-screen flex flex-col bg-white dark:bg-[#1A1A1A] text-black dark:text-white">
            <MobileHeader title="My Library" isBackBtn={true} />

            <div className="flex flex-1 overflow-hidden">
                <Sidebar />

                <main className="flex-1 min-h-0 overflow-y-auto pt-5 pb-24 md:pb-20 px-6">

                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex-shrink-0 md:block hidden"
                        >
                            <img
                                src={ICONS.arrowBack}
                                className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert"
                                alt="Back"
                            />
                        </button>

                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
                            My Library
                        </h2>
                        <div className="w-5" />
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2 mt-8 mb-2">
                        {["Purchased", "Bookmarked"].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setMainTab(tab)}
                                className={`flex-1 px-0 py-2 rounded-lg font-semibold transition min-w-0 
                                ${mainTab === tab
                                        ? "bg-[#327691] text-white"
                                        : "bg-transparent text-black dark:text-white"
                                    }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="flex items-center md:mt-6 mt-2 gap-x-4">
                        <div className="relative w-full">
                            <input
                                type="text"
                                placeholder="Search here..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="rounded-xl pl-5 pr-10 py-3 bg-[#F5F9FE] dark:bg-white placeholder-[#6b7280] outline-none w-full text-[#262626] dark:text-[#262626] border border-gray-200 dark:border-none"
                            />
                            <CiSearch className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-500 text-2xl" />
                        </div>
                    </div>

                    {/* LIST */}
                    <div className="mt-6 space-y-4">
                        {loading ? (
                            <LibrarySkeleton />
                        ) : filteredList.length === 0 ? (
                            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                                No item found.
                            </div>
                        ) : (
                            filteredList.map((item) => {
                                const creator = users[item.userId]?.name || "Unknown";
                                const isLoaded = loadedImages[item.id];

                                return (
                                    <div
                                        key={item.id}
                                        className="relative flex items-center justify-between border-t text-black dark:border-[#D9D9D9]/20 border-gray-200 dark:text-white px-4 py-4 hover:bg-gray-100 dark:hover:bg-[#2A2A2A] transition cursor-pointer hover:rounded-md"
                                        onClick={() =>
                                            navigate(`${ROUTES.PRODUCT_DETAILS}/${item.productId}`)
                                        }
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            setContextMenu({
                                                visible: true,
                                                x: e.pageX,
                                                y: e.pageY,
                                                link: `${window.location.origin}/#${ROUTES.PRODUCT_DETAILS}/${item.productId}`,
                                            });
                                        }}
                                    >
                                        {/* Left */}
                                        <div className="flex items-center gap-4 max-w-[60%]">
                                            <div className="relative w-16 h-16">
                                                {!isLoaded && (
                                                    <div className="absolute inset-0 bg-gray-300 dark:bg-gray-700 rounded-lg animate-pulse" />
                                                )}

                                                <img
                                                    src={item.coverImageUrl || IMAGES.courses}
                                                    alt="Course"
                                                    className={`w-16 h-16 rounded-lg object-cover transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"
                                                        }`}
                                                    onLoad={() => handleImageLoad(item.id)}
                                                    onError={() => handleImageLoad(item.id)}
                                                />
                                            </div>

                                            <div>
                                                <p className="text-sm text-gray-700 dark:text-white/80 line-clamp-2">
                                                    {formatDate(item.createdAt || item.addedAt)}
                                                </p>

                                                <h3 className="font-bold text-lg line-clamp-1">
                                                    {item.title}
                                                </h3>

                                                <p className="text-xs text-gray-500 dark:text-white/50 mt-1">
                                                    By {creator}
                                                </p>
                                            </div>
                                        </div>

                                         {/* Right arrow and menu */}
                                        <div className="flex items-center gap-2">
                                            <div className="relative">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenMenuId(openMenuId === item.id ? null : item.id);
                                                    }}
                                                    className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                                >
                                                    <BsThreeDotsVertical className="text-gray-700 dark:text-white text-lg" />
                                                </button>

                                                {openMenuId === item.id && (
                                                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1F1F1F] shadow-xl rounded-xl border border-gray-200 dark:border-gray-700 py-2 z-50 animate-in fade-in zoom-in duration-200">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const shareLink = `${window.location.origin}/#${ROUTES.PREVIEW_PRODUCT_DETAILS.replace(':productId', item.productId)}`;
                                                                copyToClipboard(shareLink);
                                                                window.showToast("Link copied to clipboard!", "success");
                                                                setOpenMenuId(null);
                                                            }}
                                                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
                                                        >
                                                            <FiCopy className="w-4 h-4" />
                                                            Copy Link
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleShare(item);
                                                                setOpenMenuId(null);
                                                            }}
                                                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
                                                        >
                                                            <FiShare2 className="w-4 h-4" />
                                                            Share Externally
                                                        </button>
                                                        {mainTab === "Bookmarked" && (
                                                            <button
                                                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-red-500 flex items-center gap-2"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveFromBookmarks(item.id);
                                                                    setOpenMenuId(null);
                                                                }}
                                                            >
                                                                <FiTrash2 className="w-4 h-4" />
                                                                Remove Bookmark
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {mainTab !== "Bookmarked" && (
                                                <FiChevronRight className="text-gray-700 dark:text-white text-xl cursor-pointer" />
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Context menu */}
                    {contextMenu.visible && (
                        <div
                            className="absolute z-50 bg-white dark:bg-[#2A2A2A] border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-2"
                            style={{ top: contextMenu.y, left: contextMenu.x }}
                        >
                            <button
                                className="block w-full text-left px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-[#3A3A3A] rounded"
                                onClick={() => {
                                    navigator.clipboard.writeText(contextMenu.link);
                                    window.showToast("Link copied!", "success");
                                    setContextMenu({ ...contextMenu, visible: false });
                                }}
                            >
                                Copy Link
                            </button>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default MyLibrary;
